import type { ApprovalChainAssigneeOption, ApprovalChainStepRoleOption } from '../../model/schema';
import type { ApproverOption, StepRoleCatalogue } from './ApproverPicker.types';

/**
 * Best-effort display name for an assignee. Edit-hydration can leave the name
 * empty (the response types display fields as opaque objects); fall back to the
 * email, then the id, so chips + `getInitials` never receive an empty string.
 */
export function assigneeLabel(user: ApprovalChainAssigneeOption): string {
  return user.name || user.email || user.userId;
}

export function approverOptionValue(option: ApproverOption): string {
  switch (option.kind) {
    case 'role':
      return `role:${option.role.id}`;
    case 'person':
      return `person:${option.person.userId}`;
  }
}

/** The chips a step shows: its role first, then its people. */
export function selectedApprovers(
  role: ApprovalChainStepRoleOption | null,
  people: ApprovalChainAssigneeOption[],
): ApproverOption[] {
  const roleChip: ApproverOption[] = role ? [{ kind: 'role', role }] : [];
  return [...roleChip, ...people.map((person) => ({ kind: 'person' as const, person }))];
}

/**
 * Splits a picker selection back into the step's fields. A step names one role at most, so
 * picking a second replaces the first.
 */
export function splitSelection(selection: ApproverOption[]): {
  role: ApprovalChainStepRoleOption | null;
  people: ApprovalChainAssigneeOption[];
} {
  const roles = selection.flatMap((option) => (option.kind === 'role' ? [option.role] : []));
  const people = selection.flatMap((option) => (option.kind === 'person' ? [option.person] : []));
  return { role: roles.at(-1) ?? null, people };
}

/** The role options the picker offers, or `null` when it should offer none. */
export function roleOptions(catalogue: StepRoleCatalogue): ApproverOption[] | null {
  switch (catalogue.kind) {
    case 'ready':
      return catalogue.roles.length > 0
        ? catalogue.roles.map((role) => ({ kind: 'role', role }))
        : null;
    case 'loading':
    case 'unavailable':
      return null;
  }
}

/** Whether to warn that nobody can approve through the role yet. */
export function roleHasNoHolders(
  catalogue: StepRoleCatalogue,
  role: ApprovalChainStepRoleOption | null,
): boolean {
  if (!role || catalogue.kind !== 'ready' || !catalogue.holders) return false;
  return (catalogue.holders.get(role.id)?.size ?? 0) === 0;
}

/** Dedupe assignee options by `userId`, keeping the first occurrence. */
export function dedupeByUserId(
  options: ApprovalChainAssigneeOption[],
): ApprovalChainAssigneeOption[] {
  const byId = new Map<string, ApprovalChainAssigneeOption>();
  for (const option of options) {
    if (!byId.has(option.userId)) byId.set(option.userId, option);
  }
  return [...byId.values()];
}

export function approverOptionLabel(option: ApproverOption): string {
  switch (option.kind) {
    case 'role':
      return option.role.name;
    case 'person':
      return assigneeLabel(option.person);
  }
}
